import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { isUrlValid } from "../../../../utils/utils";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import addUserButton from "./images/ImpervaWAF_add_user_button.png";
import copyApiKeydetails from "./images/ImpervaWAF_copy_api_key_details.png";
import createApiKey from "./images/ImpervaWAF_create_api_key.png";
import createUser from "./images/ImpervaWAF_create_user.png";
import editUser from "./images/ImpervaWAF_edit_user.png";
import enterSettings from "./images/ImpervaWAF_enter_settings.png";
import makeApiKey from "./images/ImpervaWAF_make_api_key.png";
import saveIpAddresses from "./images/ImpervaWAF_save_ip_addresses.png";
import setAsApiOnlyUser from "./images/ImpervaWAF_set_as_api_only_user.png";

const IMPERVA_WAF_CODE_NAME = "imperva_waf";

export default function ImpervaWAFIntegration() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [consoleUrl, setConsoleUrl] = useState("");
  const [apiKeyId, setApiKeyId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showConsoleUrlError, setShowConsoleUrlError] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const { registerImpervaWAFIntegration } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    IMPERVA_WAF_CODE_NAME
  );
  const { staticIPs: allowlistIps } = useStaticIPs();
  const guideMode = searchParams.has("viewonly");
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const normalizeConsoleUrl = (url) => {
    if (!url) return url;
    let normalizedUrl = url.trim();

    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    try {
      const parsedUrl = new URL(normalizedUrl);
      return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
    } catch (e) {
      return normalizedUrl;
    }
  };

  const onSubmit = async () => {
    const normalizedUrl = normalizeConsoleUrl(consoleUrl);

    if (!isUrlValid(normalizedUrl)) {
      setShowConsoleUrlError(true);
      return;
    }
    setRegisterIsLoading(true);
    try {
      await registerImpervaWAFIntegration(apiKeyId, apiKey, normalizedUrl);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Imperva WAF registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  const handleConsoleUrlChange = (e) => {
    setConsoleUrl(e.target.value);
    setShowConsoleUrlError(false);
  };

  return (
    <Box>
      <Guideline
        image={logos.ImpervaWAFLogo}
        name={"Imperva WAF"}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Imperva WAF is integrated!</Typography>
            </Box>
          ) : (
            <Box>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Imperva WAF">
                  <TextWithExternalLink
                    text="Open your"
                    linkText="Imperva WAF console"
                    href="https://my.imperva.com"
                  />
                </GuidelineStep>
                <GuidelineStep title="Create a new user">
                  <Typography variant="body2">
                    Click on <b>Account -{">"} Account Management</b> and create
                    a new user.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: enterSettings,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click on{" "}
                    <b>
                      User Management -{">"} Users -{">"} Add New User
                    </b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: addUserButton,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Assign an email - the email is not used so any email address
                    will do, even non-existent works.
                  </Typography>
                  <Typography variant="body2">
                    Give the user a 'Reader' role.
                  </Typography>
                  <Typography variant="body2">
                    Click the <b>Add User</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: createUser,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Set as API Only user">
                  <Typography variant="body2">
                    Click the 3 dots next to the user and select{" "}
                    <b>Set as API-only user</b>, then click <b>Ok</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: setAsApiOnlyUser,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Create API Key">
                  <Typography variant="body2">
                    Click the 3 dots next to the user, select <b>Edit</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: editUser,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Navigate to the <b>API Keys</b> section. Click{" "}
                    <b>Add API Key</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: createApiKey,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Set the expiration date to 'Never' or '1 year'. Make the
                    status 'Enabled' then Click the <b>Create</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: makeApiKey,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Copy the <b>API Key ID</b> and <b>API Key</b> values and
                    paste them below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: copyApiKeydetails,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Configure allowed IP addresses">
                  <Typography variant="body2">
                    Click the <b>Approved IP Addresses</b> tab. Add the
                    following Breeze IP addresses to the allowlist:
                  </Typography>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        marginLeft: "20px",
                      }}
                    >
                      <Typography variant="body1">{ip}</Typography>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                  <Typography variant="body2">
                    Click the <b>Save</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: saveIpAddresses,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Enter your credentials">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <TextField
                      placeholder="Console URL (e.g., https://management.service.imperva.com/)"
                      fullWidth
                      type="text"
                      value={consoleUrl}
                      onChange={handleConsoleUrlChange}
                      disabled={guideMode}
                      error={showConsoleUrlError}
                      helperText={
                        showConsoleUrlError &&
                        "Please enter a valid Imperva WAF console URL (e.g., https://my.imperva.com)"
                      }
                    />
                    <TextField
                      placeholder="API Key ID"
                      fullWidth
                      type="text"
                      value={apiKeyId}
                      onChange={(e) => setApiKeyId(e.target.value)}
                      disabled={guideMode}
                    />
                    <TextField
                      placeholder="API Key"
                      fullWidth
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle api key visibility"
                              onClick={() => setShowApiKey((prev) => !prev)}
                            >
                              {showApiKey ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ContainedButton
                      onClick={onSubmit}
                      size="small"
                      color="primary"
                      disabled={
                        guideMode || !apiKeyId || !apiKey || !consoleUrl
                      }
                      loading={registerIsLoading}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
}
